import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import { Tag } from "antd";
import BreadCrumb from "../../../common/BreadCrumb";
import TableContainer from "../../../common/table/TableContainer";
import { useQuery, useQueryClient } from "react-query";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal, Spin } from "antd";
import HistoryContext from "../../../common/HistoryContext";
import toast from "react-hot-toast";
import {
  getPaymentsByMerchantId,
  deletePaymentAccount,
  getPaymentMethodsByCountry,
} from "../services/paymentApi";
import "../styles/style.css";
//import AddEditDevForm from "../component/addEditDevForm";
import DetailPayment from "../component/detailPayment";
import AddEditPaymentModal from "../component/addEditPaymentModal";
import { PaymentProvider } from "../../../common/PaymentContext";
import DetailPaymentAccount from "../component/detailPaymentAccount";

const PaymentAccounts = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();

  document.title = `Versements | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPaymentAccount, setselectedPaymentAccount] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetRegistrationNumber, setDeleteTargetRegistrationNumber] =
    useState("");

  const currentuser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchandId = currentuser?.user?.id;

  const {
    isLoading,
    isError,
    error,
    data: paymentAccounts,
  } = useQuery(
    ["paymentAccounts", merchandId],
    () => getPaymentsByMerchantId(merchandId),
    {
      enabled: !!merchandId,
    }
  );

  const {
    isLoadingPaymentMethod,
    isErrorPaymentMethod,
    errorOPaymentMethod,
    data: paymentMethods,
  } = useQuery(
    ["paymentMethods", merchandId],
    () => getPaymentMethodsByCountry(),
    {
      enabled: !!merchandId,
    }
  );

  const tabs = ["Tous", "Comptes actifs", "Comptes non actifs"];
  const [activeTab, setActiveTab] = useState("Tous");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const filteredDevelopers = useMemo(() => {
    if (!paymentAccounts) return error?.message || error || [];
    switch (activeTab) {
      case "Comptes actifs":
        return paymentAccounts.filter((v) => v.type === "ACTIVE");
      case "Comptes non actifs":
        return paymentAccounts.filter((v) => v.type === "DISABLED");
      default:
        return paymentAccounts;
    }
  }, [paymentAccounts, activeTab]);

  const toggleAddEditModal = useCallback(
    (paymentAccount = null) => {
      setselectedPaymentAccount(paymentAccount);
      setIsAddEditModalOpen((prev) => !prev);
      if (isDetailModalOpen) setIsDetailModalOpen(false);
    },
    [isDetailModalOpen]
  );

  const handleEdit = (paymentAccount) => {
    toggleAddEditModal(paymentAccount);
  };

  const toggleDetailModal = useCallback(
    (paymentAccount = null) => {
      setselectedPaymentAccount(paymentAccount);
      setIsDetailModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    },
    [isAddEditModalOpen]
  );

  const handleDetail = (payment) => {
    toggleDetailModal(payment);
  };

  const handleDelete = async (paymentAccountId) => {
    try {
      await deletePaymentAccount(paymentAccountId, merchandId);
      queryClient.invalidateQueries("paymentAccounts");
      toast.success("Compte supprimé avec succès.");
    } catch (error) {
      toast.error(
        `😔 Erreur lors de la suppression du développeur : ${
          error?.message || error
        }`
      );
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (paymentAccountId) => {
    const paymentAccountToDelete = paymentAccounts.find(
      (paymentAccount) => paymentAccount.id === paymentAccountId
    );
    if (paymentAccountToDelete) {
      setDeleteTargetId(paymentAccountId);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Numéro du compte",
        accessor: "accountNumber",
        filterable: false,
        Cell: ({ value, row }) => {
          if (row.original.type == "WALLET")
            return <Tag color="blue"> {value} </Tag>;
          return <Tag color="orange"> {value} </Tag>;
        },
      },
      {
        Header: "Passerelle de paiement",
        accessor: "methodPayment",
        filterable: false,
        Cell: ({ value }) => {
          return `${value.name} (${value.country})`;
        },
      },
      { Header: "Montant Total (XOF)", accessor: "amount", filterable: false },
      {
        Header: "Date de création",
        accessor: "createAt",
        Cell: ({ value }) =>
          value
            ? new Date(value).toLocaleDateString("fr-FR") +
              " " +
              new Date(value).toLocaleTimeString()
            : "-",

        filterable: false,
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              title="Détail"
              size="sm"
              onClick={() => handleDetail(row.original)}
              className="btn btn-soft-info"
            >
              <EyeOutlined />
            </Button>
            <Button
              title="Comptes développeurs associés"
              size="sm"
              onClick={() => handleEdit(row.original)}
              className="btn btn-soft-warning"
            >
              <LinkOutlined />
            </Button>
            <Button
              title="Supprimer"
              size="sm"
              onClick={() => openDeleteConfirmModal(row.original.id)}
              className="btn btn-soft-danger"
            >
              <DeleteOutlined />
            </Button>
          </div>
        ),
        filterable: false,
      },
    ],
    [handleEdit, openDeleteConfirmModal, handleDetail]
  );

  const renderTabs = () => (
    <div className="d-flex gap-3 mb-3">
      {tabs.map((tab) => (
        <Button
          key={tab}
          onClick={() => handleTabChange(tab)}
          className={`btn ${activeTab == tab ? "active-tab" : "btn-light"}`}
        >
          {tab}
        </Button>
      ))}
    </div>
  );

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">{renderTabs()}</div>
      <Button
        type="button"
        className="active-tab"
        icon={<PlusCircleOutlined />}
        title="Développeurs"
        onClick={() => toggleAddEditModal(null)}
      >
        Ajouter un compte de versement
      </Button>
    </div>
  );
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  return (
    <PaymentProvider>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Versements" history={history} />
          <Row>
            <Col>
              <Card
                id="contactList"
                className="table-payments-container"
                style={{ marginTop: "2%" }}
              >
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <TableContainer
                    columns={columns}
                    data={filteredDevelopers}
                    isGlobalSearch={true}
                    isGlobalFilter={true}
                    customPageSize={5}
                    className="custom-header-css"
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light"
                    SearchPlaceholder="Recherche..."
                    HeaderTable={HeaderTable}
                  />
                  {isLoading && !isError && (
                    <div className="text-center">
                      Chargement, Merci de patienter ... <Spin />
                    </div>
                  )}
                  {filteredDevelopers &&
                    filteredDevelopers.length === 0 &&
                    !isLoading && (
                      <div className="text-center">
                        Aucun développeur pour ce marchand.
                      </div>
                    )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <AddEditPaymentModal
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedPaymentAccount={selectedPaymentAccount}
            currentuser={currentuser}
          />
          <DetailPaymentAccount
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            toggle={toggleDetailModal}
            selectedPaymentAccount={selectedPaymentAccount}
            currentuser={currentuser}
          />

          <Modal
            title="Confirmer la suppression du développeur"
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button
                key="cancel"
                onClick={closeDeleteConfirmModal}
                className="btn btn-soft-primary"
              >
                Annuler
              </Button>,
              <Button
                key="delete"
                type="danger"
                className="btn btn-soft-danger"
                onClick={() => handleDelete(deleteTargetId)}
              >
                Oui, Supprime
              </Button>,
            ]}
          >
            <p>Êtes-vous sûr de vouloir supprimer le compte ?</p>
          </Modal>
        </Container>
      </div>
    </PaymentProvider>
  );
};

export default PaymentAccounts;
